import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Link from '@rad/components/utility/link';
import NotificationsBell from './notifications-bell';
import UserMenu from './user-menu';
import CompleteSignupDropdown from './complete-signup-dropdown';
import { useAuth } from '../../hooks/useAuth';
import useDistrict from '../../hooks/useDistrict';
import { districtUrls } from '../../common/routes/urls/district';
import { exploreUrls, generalUrls } from '../../common/routes/urls/general';
import { getBaseURL } from '../../common/routes/_';
import { withoutCommunityPath } from '../../common/community-path';
// Words that join a name rather than name anything, so they give no initial.
const JOINING_WORDS = new Set(['a', 'an', 'and', 'at', 'for', 'in', 'of', 'on', 'the']);
// Words most community names share. A name made only of these gives initials that identify nobody.
const GENERIC_WORDS = new Set([
    'association', 'club', 'committee', 'community', 'council', 'district', 'foundation', 'group',
    'heritage', 'historic', 'historical', 'organization', 'preservation', 'society', 'trust',
]);
// More than this many no longer read as initials, nor fit the badge.
const MAX_INITIALS = 3;
// The initials of a community's name, or null where they would be unclear: a name with no words, one
// too long to abbreviate, a word that does not start with a letter, or nothing but generic words.
const initialsOf = (title) => {
    const words = title
        .split(/\s+/)
        .map(word => word.replace(/^[^\p{L}\p{N}]+/u, ''))
        .filter(word => word !== '' && !JOINING_WORDS.has(word.toLowerCase()));
    if (words.length === 0 || words.length > MAX_INITIALS)
        return null;
    if (words.some(word => !/^\p{L}/u.test(word)))
        return null;
    if (words.every(word => GENERIC_WORDS.has(word.toLowerCase())))
        return null;
    return words.map(word => word.charAt(0).toUpperCase()).join('');
};
// The community's logo when it has one, which carries its name: the whole mark on wider screens and
// its leading square, where a mark keeps its emblem, on phones. Without one, a badge of its initials
// (or a building, where the name gives none worth showing) and its name, which phones leave out.
const TourBrand = (props) => {
    const logoUrl = props.district.logoUrl;
    if (logoUrl) {
        return _jsx("img", { src: logoUrl, alt: `${props.district.title} logo`, className: "tour-navbar-logo" });
    }
    const initials = initialsOf(props.district.title);
    return _jsxs(_Fragment, { children: [_jsx("span", { className: "tour-navbar-badge", "aria-hidden": "true", children: initials !== null && initials !== void 0 ? initials : _jsx("i", { className: "bi bi-bank" }) }), props.showName && (_jsx("span", { className: "tour-navbar-name text-truncate d-none d-sm-inline", children: props.district.title }))] });
};
// A tour's only navigation bar. In place of the community's own, it keeps visitors on the tour: the
// community's mark leads home, and the rest is finding other communities, notifications, and the account.
const TourNavbar = (props) => {
    const { displaySettings } = useDistrict();
    const [auth] = useAuth();
    const showName = displaySettings['show-district-name'] !== 'false';
    return (_jsx("nav", { className: "navbar district-navbar tour-navbar", children: _jsxs("div", { className: "container flex-nowrap gap-3", children: [_jsx(Link, { href: districtUrls.home.getURL(), title: "Back to home", "aria-label": props.district.title, className: "tour-navbar-brand d-flex align-items-center gap-2 text-decoration-none", children: _jsx(TourBrand, { district: props.district, showName: showName }) }), _jsxs("div", { className: "d-flex align-items-center gap-3 flex-shrink-0", children: [_jsx("a", { href: getBaseURL() + withoutCommunityPath(exploreUrls.explore.getURL()), className: "tour-navbar-search", title: "View More Districts", "aria-label": "View More Districts", children: _jsx("i", { className: "bi bi-search fs-5", "aria-hidden": "true" }) }), auth.isAuthenticated ? (auth.isRegistered ? (_jsxs(_Fragment, { children: [_jsx(NotificationsBell, {}), _jsx(UserMenu, { triggerClassName: "nav-link px-0 d-flex align-items-center gap-2" })] })) : (_jsx(CompleteSignupDropdown, {}))) : (_jsx(Link, { href: generalUrls.login.getURL(), className: "topbar-login-btn", children: "Login" }))] })] }) }));
};
export default TourNavbar;
